/**
 * Unified Color Scheme Configuration
 *
 * Single source of truth for color schemes across all navigation/menu components.
 * Combines properties from SettingsSidebar, BreadcrumbTokens, and SubMenu.
 *
 * Usage:
 * - import { colorSchemes, getColorScheme } from '@/lib/ui/colorSchemes';
 * - const scheme = getColorScheme('blue');
 * - className={scheme.activeBg}
 */

import type { NavColorScheme } from '@/lib/navigation/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Complete color scheme configuration
 * Supports all navigation component use cases
 */
export interface ColorSchemeConfig {
  // Active state (when item is selected/current)
  activeBg: string;
  activeText: string;
  activeBorder: string;

  // Icon styling
  iconColor: string;
  iconBg: string;

  // Hover state
  hoverBg: string;
  hoverText: string;

  // Indicators
  dot: string;
  indicatorBg: string;

  // Additional properties for breadcrumb/submenu compatibility
  text: string;
  bg: string;
  border: string;
}

// ============================================================================
// Color Scheme Definitions
// ============================================================================

/**
 * 21 color schemes matching Tailwind color palette
 */
export const colorSchemes: Record<NavColorScheme, ColorSchemeConfig> = {
  blue: {
    activeBg: 'bg-blue-500/10',
    activeText: 'text-blue-400',
    activeBorder: 'border-blue-400',
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-500/10',
    hoverBg: 'hover:bg-blue-500/5',
    hoverText: 'group-hover:text-blue-400',
    dot: 'bg-blue-400',
    indicatorBg: 'bg-blue-400',
    text: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
  },
  amber: {
    activeBg: 'bg-amber-500/10',
    activeText: 'text-amber-400',
    activeBorder: 'border-amber-400',
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/10',
    hoverBg: 'hover:bg-amber-500/5',
    hoverText: 'group-hover:text-amber-400',
    dot: 'bg-amber-400',
    indicatorBg: 'bg-amber-400',
    text: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
  },
  emerald: {
    activeBg: 'bg-emerald-500/10',
    activeText: 'text-emerald-400',
    activeBorder: 'border-emerald-400',
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/10',
    hoverBg: 'hover:bg-emerald-500/5',
    hoverText: 'group-hover:text-emerald-400',
    dot: 'bg-emerald-400',
    indicatorBg: 'bg-emerald-400',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
  },
  cyan: {
    activeBg: 'bg-cyan-500/10',
    activeText: 'text-cyan-400',
    activeBorder: 'border-cyan-400',
    iconColor: 'text-cyan-400',
    iconBg: 'bg-cyan-500/10',
    hoverBg: 'hover:bg-cyan-500/5',
    hoverText: 'group-hover:text-cyan-400',
    dot: 'bg-cyan-400',
    indicatorBg: 'bg-cyan-400',
    text: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
  },
  purple: {
    activeBg: 'bg-purple-500/10',
    activeText: 'text-purple-400',
    activeBorder: 'border-purple-400',
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-500/10',
    hoverBg: 'hover:bg-purple-500/5',
    hoverText: 'group-hover:text-purple-400',
    dot: 'bg-purple-400',
    indicatorBg: 'bg-purple-400',
    text: 'text-purple-400',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/20',
  },
  rose: {
    activeBg: 'bg-rose-500/10',
    activeText: 'text-rose-400',
    activeBorder: 'border-rose-400',
    iconColor: 'text-rose-400',
    iconBg: 'bg-rose-500/10',
    hoverBg: 'hover:bg-rose-500/5',
    hoverText: 'group-hover:text-rose-400',
    dot: 'bg-rose-400',
    indicatorBg: 'bg-rose-400',
    text: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/20',
  },
  slate: {
    activeBg: 'bg-slate-500/10',
    activeText: 'text-slate-400',
    activeBorder: 'border-slate-400',
    iconColor: 'text-slate-400',
    iconBg: 'bg-slate-500/10',
    hoverBg: 'hover:bg-slate-500/5',
    hoverText: 'group-hover:text-slate-400',
    dot: 'bg-slate-400',
    indicatorBg: 'bg-slate-400',
    text: 'text-slate-400',
    bg: 'bg-slate-500/10',
    border: 'border-slate-500/20',
  },
  zinc: {
    activeBg: 'bg-zinc-500/10',
    activeText: 'text-zinc-400',
    activeBorder: 'border-zinc-400',
    iconColor: 'text-zinc-400',
    iconBg: 'bg-zinc-500/10',
    hoverBg: 'hover:bg-zinc-500/5',
    hoverText: 'group-hover:text-zinc-400',
    dot: 'bg-zinc-400',
    indicatorBg: 'bg-zinc-400',
    text: 'text-zinc-400',
    bg: 'bg-zinc-500/10',
    border: 'border-zinc-500/20',
  },
  neutral: {
    activeBg: 'bg-neutral-500/10',
    activeText: 'text-neutral-400',
    activeBorder: 'border-neutral-400',
    iconColor: 'text-neutral-400',
    iconBg: 'bg-neutral-500/10',
    hoverBg: 'hover:bg-neutral-500/5',
    hoverText: 'group-hover:text-neutral-400',
    dot: 'bg-neutral-400',
    indicatorBg: 'bg-neutral-400',
    text: 'text-neutral-400',
    bg: 'bg-neutral-500/10',
    border: 'border-neutral-500/20',
  },
  stone: {
    activeBg: 'bg-stone-500/10',
    activeText: 'text-stone-400',
    activeBorder: 'border-stone-400',
    iconColor: 'text-stone-400',
    iconBg: 'bg-stone-500/10',
    hoverBg: 'hover:bg-stone-500/5',
    hoverText: 'group-hover:text-stone-400',
    dot: 'bg-stone-400',
    indicatorBg: 'bg-stone-400',
    text: 'text-stone-400',
    bg: 'bg-stone-500/10',
    border: 'border-stone-500/20',
  },
  red: {
    activeBg: 'bg-red-500/10',
    activeText: 'text-red-400',
    activeBorder: 'border-red-400',
    iconColor: 'text-red-400',
    iconBg: 'bg-red-500/10',
    hoverBg: 'hover:bg-red-500/5',
    hoverText: 'group-hover:text-red-400',
    dot: 'bg-red-400',
    indicatorBg: 'bg-red-400',
    text: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
  },
  orange: {
    activeBg: 'bg-orange-500/10',
    activeText: 'text-orange-400',
    activeBorder: 'border-orange-400',
    iconColor: 'text-orange-400',
    iconBg: 'bg-orange-500/10',
    hoverBg: 'hover:bg-orange-500/5',
    hoverText: 'group-hover:text-orange-400',
    dot: 'bg-orange-400',
    indicatorBg: 'bg-orange-400',
    text: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/20',
  },
  yellow: {
    activeBg: 'bg-yellow-500/10',
    activeText: 'text-yellow-400',
    activeBorder: 'border-yellow-400',
    iconColor: 'text-yellow-400',
    iconBg: 'bg-yellow-500/10',
    hoverBg: 'hover:bg-yellow-500/5',
    hoverText: 'group-hover:text-yellow-400',
    dot: 'bg-yellow-400',
    indicatorBg: 'bg-yellow-400',
    text: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/20',
  },
  lime: {
    activeBg: 'bg-lime-500/10',
    activeText: 'text-lime-400',
    activeBorder: 'border-lime-400',
    iconColor: 'text-lime-400',
    iconBg: 'bg-lime-500/10',
    hoverBg: 'hover:bg-lime-500/5',
    hoverText: 'group-hover:text-lime-400',
    dot: 'bg-lime-400',
    indicatorBg: 'bg-lime-400',
    text: 'text-lime-400',
    bg: 'bg-lime-500/10',
    border: 'border-lime-500/20',
  },
  green: {
    activeBg: 'bg-green-500/10',
    activeText: 'text-green-400',
    activeBorder: 'border-green-400',
    iconColor: 'text-green-400',
    iconBg: 'bg-green-500/10',
    hoverBg: 'hover:bg-green-500/5',
    hoverText: 'group-hover:text-green-400',
    dot: 'bg-green-400',
    indicatorBg: 'bg-green-400',
    text: 'text-green-400',
    bg: 'bg-green-500/10',
    border: 'border-green-500/20',
  },
  teal: {
    activeBg: 'bg-teal-500/10',
    activeText: 'text-teal-400',
    activeBorder: 'border-teal-400',
    iconColor: 'text-teal-400',
    iconBg: 'bg-teal-500/10',
    hoverBg: 'hover:bg-teal-500/5',
    hoverText: 'group-hover:text-teal-400',
    dot: 'bg-teal-400',
    indicatorBg: 'bg-teal-400',
    text: 'text-teal-400',
    bg: 'bg-teal-500/10',
    border: 'border-teal-500/20',
  },
  sky: {
    activeBg: 'bg-sky-500/10',
    activeText: 'text-sky-400',
    activeBorder: 'border-sky-400',
    iconColor: 'text-sky-400',
    iconBg: 'bg-sky-500/10',
    hoverBg: 'hover:bg-sky-500/5',
    hoverText: 'group-hover:text-sky-400',
    dot: 'bg-sky-400',
    indicatorBg: 'bg-sky-400',
    text: 'text-sky-400',
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/20',
  },
  indigo: {
    activeBg: 'bg-indigo-500/10',
    activeText: 'text-indigo-400',
    activeBorder: 'border-indigo-400',
    iconColor: 'text-indigo-400',
    iconBg: 'bg-indigo-500/10',
    hoverBg: 'hover:bg-indigo-500/5',
    hoverText: 'group-hover:text-indigo-400',
    dot: 'bg-indigo-400',
    indicatorBg: 'bg-indigo-400',
    text: 'text-indigo-400',
    bg: 'bg-indigo-500/10',
    border: 'border-indigo-500/20',
  },
  violet: {
    activeBg: 'bg-violet-500/10',
    activeText: 'text-violet-400',
    activeBorder: 'border-violet-400',
    iconColor: 'text-violet-400',
    iconBg: 'bg-violet-500/10',
    hoverBg: 'hover:bg-violet-500/5',
    hoverText: 'group-hover:text-violet-400',
    dot: 'bg-violet-400',
    indicatorBg: 'bg-violet-400',
    text: 'text-violet-400',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/20',
  },
  fuchsia: {
    activeBg: 'bg-fuchsia-500/10',
    activeText: 'text-fuchsia-400',
    activeBorder: 'border-fuchsia-400',
    iconColor: 'text-fuchsia-400',
    iconBg: 'bg-fuchsia-500/10',
    hoverBg: 'hover:bg-fuchsia-500/5',
    hoverText: 'group-hover:text-fuchsia-400',
    dot: 'bg-fuchsia-400',
    indicatorBg: 'bg-fuchsia-400',
    text: 'text-fuchsia-400',
    bg: 'bg-fuchsia-500/10',
    border: 'border-fuchsia-500/20',
  },
  pink: {
    activeBg: 'bg-pink-500/10',
    activeText: 'text-pink-400',
    activeBorder: 'border-pink-400',
    iconColor: 'text-pink-400',
    iconBg: 'bg-pink-500/10',
    hoverBg: 'hover:bg-pink-500/5',
    hoverText: 'group-hover:text-pink-400',
    dot: 'bg-pink-400',
    indicatorBg: 'bg-pink-400',
    text: 'text-pink-400',
    bg: 'bg-pink-500/10',
    border: 'border-pink-500/20',
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get color scheme with fallback to blue
 */
export function getColorScheme(
  colorScheme: NavColorScheme | undefined,
  fallback: NavColorScheme = 'blue'
): ColorSchemeConfig {
  return colorSchemes[colorScheme || fallback];
}

/**
 * Get classes for a current item with category coloring (breadcrumb compatibility)
 */
export function getCurrentWithColor(color: NavColorScheme): string {
  const scheme = getColorScheme(color);
  return `${scheme.text} ${scheme.bg} font-medium px-2 py-1 rounded-md`;
}

// ============================================================================
// Legacy Aliases (for backwards compatibility during migration)
// ============================================================================

/**
 * @deprecated Use colorSchemes directly
 * Alias for breadcrumb compatibility
 */
export const breadcrumbColorSchemes = colorSchemes;

// Re-export NavColorScheme type for convenience
export type { NavColorScheme };
